import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { Badge } from '../../../ui-components/ui/badge';
import { Avatar, AvatarFallback } from '../../../ui-components/ui/avatar';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../../lib/utils/utils';

interface RaceParticipant {
  id: string;
  name: string;
  initials: string;
  rank: number;
  previousRank: number;
  salesAmount: number;
  leadsCount: number;
  conversionRate: number;
  profilePicture?: string;
}

const RaceList: React.FC = () => {
  // Mock data - in real app this would come from API
  const participants: RaceParticipant[] = [
    {
      id: '1',
      name: 'Max Mustermann',
      initials: 'MM',
      rank: 1,
      previousRank: 2,
      salesAmount: 125430,
      leadsCount: 45,
      conversionRate: 18.5,
    },
    {
      id: '2',
      name: 'Anna Schmidt',
      initials: 'AS',
      rank: 2,
      previousRank: 1,
      salesAmount: 118290,
      leadsCount: 52,
      conversionRate: 15.2,
    },
    {
      id: '3',
      name: 'Thomas Weber',
      initials: 'TW',
      rank: 3,
      previousRank: 4,
      salesAmount: 98760,
      leadsCount: 38,
      conversionRate: 17.3,
    },
    {
      id: '4',
      name: 'Julia Fischer',
      initials: 'JF',
      rank: 4,
      previousRank: 3,
      salesAmount: 87650,
      leadsCount: 41,
      conversionRate: 14.2,
    },
    {
      id: '5',
      name: 'Michael Bauer',
      initials: 'MB',
      rank: 5,
      previousRank: 5,
      salesAmount: 76540,
      leadsCount: 35,
      conversionRate: 14.6,
    },
  ];

  const getRankChange = (current: number, previous: number) => {
    if (current < previous) return 'up';
    if (current > previous) return 'down';
    return 'same';
  };

  const getRankChangeIcon = (change: 'up' | 'down' | 'same') => {
    switch (change) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRankBadgeColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-yellow-500 text-white';
      case 2:
        return 'bg-gray-400 text-white';
      case 3:
        return 'bg-orange-600 text-white';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Verkaufs-Rennliste</CardTitle>
            <CardDescription>Top Performer diesen Monat</CardDescription>
          </div>
          <Trophy className="h-5 w-5 text-yellow-500" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {participants.map((participant) => {
            const rankChange = getRankChange(participant.rank, participant.previousRank);
            
            return (
              <div
                key={participant.id}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  participant.rank === 1 && "bg-yellow-50 border-yellow-200"
                )}
              >
                <div className="flex items-center gap-3">
                  {/* Rank Badge */}
                  <Badge 
                    className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center font-bold",
                      getRankBadgeColor(participant.rank)
                    )}
                  >
                    {participant.rank}
                  </Badge>

                  {/* Rank Change */}
                  <div className="flex items-center">
                    {getRankChangeIcon(rankChange)}
                  </div>

                  {/* User Info */}
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>{participant.initials}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{participant.name}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{participant.leadsCount} Leads</span>
                        <span>{participant.conversionRate}% CR</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sales Amount */}
                <div className="text-right">
                  <p className="font-bold text-lg">
                    €{participant.salesAmount.toLocaleString('de-DE')}
                  </p>
                  <p className="text-sm text-gray-500">Umsatz</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="mt-6 pt-4 border-t grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-500">Team Gesamt</p>
            <p className="font-bold text-lg">€506,670</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Ø Conversion</p>
            <p className="font-bold text-lg">15.8%</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Leads Total</p>
            <p className="font-bold text-lg">211</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RaceList;
